import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import {
	ScrollType,
	buildColumn,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	handleResponse,
	invertSelectedRowKeys,
	useHandleOnChangeTableLogic,
} from '../../../../../Helper';
import { deviceAdminApi } from '../../../../../api';
import { MemberColumn, MemberPaginationSettings } from '../../../../../model/CommonModel';
import { MembersModel, RoleTab } from '../../../../../model/DeviceAdminModel';
import { SearchColumn } from '../../../../common';
import { CloneFromRole } from '../CloneFromRole/CloneFromRole';
import { setMemberContextModelAction } from '../RoleContext/action';
import { RoleStore } from '../RoleContext/context';
import { AddMembersModal } from './AddMembersModal/AddMembersModal';
import { ExpandableContent } from './ExpandableContent/ExpandableContent';
import styles from './members.module.scss';

type Props = {};

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { x: 379, y: 400 };

const Members: React.FC<Props> = () => {
	//#region state management variables
	const {
		roleContextInitialState: {
			props: { roleId, roleName },
			member: { subscribedOperatorIds },
		},
		dispatcher,
	} = useContext(RoleStore);

	const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
	const [tableData, setTableData] = useState<MemberColumn[] | null>(null);
	const [tablePaginationSetting, setTablePaginationSetting] = useState<MemberPaginationSettings>({
		...getDefaultPaginationSettings(),
		RoleId: roleId,
		IncludedInList: true,
	});
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [isOpenAddNewMemberModal, setIsOpenAddNewMemberModal] = useState<boolean>(false);
	const [nonSubscribedOperatorsIds, setNonSubscribedOperatorsIds] = useState<number[]>([]);

	const isSearchPerformed = useRef<boolean>(false);
	//#endregion

	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	useEffect(() => {
		if (roleId) {
			deviceAdminApi.getMembersIdsInRole(roleId).then(response => {
				if (!handleResponse(response)) {
					const {
						Entity: { NonSubscribedOperatorIds, SubscribedOperatorIds },
					} = response;
					setNonSubscribedOperatorsIds(NonSubscribedOperatorIds);
					dispatcher(setMemberContextModelAction({ subscribedOperatorIds: SubscribedOperatorIds }));
					loadMembers(SubscribedOperatorIds, { ...tablePaginationSetting, RoleId: roleId });
				}
			});
		}
	}, [roleId]);

	useEffect(() => {
		loadMembers(subscribedOperatorIds, { ...tablePaginationSetting, RoleId: roleId });
	}, [subscribedOperatorIds]);

	const loadMembers = async (membersSelected: number[], paginationSetting: MemberPaginationSettings): Promise<void> => {
		if (!isTableLoading) {
			setIsTableLoading(true);
		}

		batch(() => {
			deviceAdminApi
				.getMembers(membersSelected, paginationSetting)
				.then(response => {
					if (!handleResponse(response)) {
						const { Members, TotalItems } = response.Entity;
						const membersMapped = mapMembers(Members);
						setTableData(membersMapped);
						setTablePaginationSetting({ ...paginationSetting, TotalItems: TotalItems });
					}
				})
				.finally(() => {
					setIsTableLoading(false);
				});
		});
	};

	const mapMembers = (data: MembersModel[]): MemberColumn[] => {
		if (data.length > 0) {
			return data.map<MemberColumn>((d: MembersModel, index: number) => ({
				...d,
				Index: index,
				key: d.OperatorId.toString(),
				Action: undefined,
			}));
		}

		return [];
	};

	const onConfirm = (newOperatorsIds: React.Key[]) => {
		batch(() => {
			setNonSubscribedOperatorsIds(nonSubscribedOperatorsIds.filter(item => !newOperatorsIds.some(rowKey => item === Number(rowKey))));
			const clonedState = [...subscribedOperatorIds, ...newOperatorsIds.map<number>(x => Number(x))];
			dispatcher(setMemberContextModelAction({ isDirty: true, subscribedOperatorIds: clonedState }));
			loadMembers(clonedState, tablePaginationSetting);
			setIsOpenAddNewMemberModal(false);
		});
	};

	const onRemoveRoleButtonClick = () => {
		batch(() => {
			const nonSubscribedOperatorsIdsClonedState = [...nonSubscribedOperatorsIds];
			nonSubscribedOperatorsIdsClonedState.push(...selectedRowKeys.map<number>(x => Number(x)));
			setNonSubscribedOperatorsIds(nonSubscribedOperatorsIdsClonedState);
			const clonedState = subscribedOperatorIds.filter(x => !selectedRowKeys.some(y => Number(y) === x));
			dispatcher(setMemberContextModelAction({ isDirty: true, subscribedOperatorIds: clonedState }));
			setSelectedRowKeys([]);
			loadMembers(clonedState, tablePaginationSetting);
		});
	};

	const clearSelection = () => {
		setSelectedRowKeys([]);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
			setTablePaginationSetting({ ...tablePaginationSetting, PageNumber: page, PageSize: pageSize });
		} else {
			setTablePaginationSetting({ ...tablePaginationSetting, PageNumber: page });
		}
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<MemberColumn>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			setIsTableLoading(true);
			loadMembers(subscribedOperatorIds, {
				...tablePaginationSetting,
				PageNumber: current,
				PageSize: pageSize,
				SortDirection: sortOrder,
				SortField: sortField.toString(),
			});
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	const setSearchPerformedSource = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const handleOnSearch = (searchString: string) => {
		loadMembers(subscribedOperatorIds, { ...tablePaginationSetting, SearchedValue: searchString });
	};

	const handleSelectAll = () => {
		setSelectedRowKeys(subscribedOperatorIds.filter(x => x !== 1).map<React.Key>(x => x.toString()));
	};

	const handleSelectInverse = () => {
		const dataKeys = tableData.map<React.Key>(x => x.key.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		setSelectedRowKeys(newSelectedRowKeys.filter(key => key !== '1'));
	};

	const rowSelectionMembers: TableRowSelection<MemberColumn> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: [
			{ key: 'SELECTION_ALL_SOURCE', text: 'Select all data', onSelect: handleSelectAll },
			{ key: 'SELECTION_INVERT_SOURCE', text: 'Invert current page', onSelect: handleSelectInverse },
			{ key: 'SELECTION_NONE', text: 'Clear all data', onSelect: clearSelection },
		],
		onChange: (selectedRowKeys: React.Key[]) => {
			setSelectedRowKeys(selectedRowKeys);
		},
		getCheckboxProps: record => {
			return {
				children: <label htmlFor={`rolesMembersInTableCheckbox-${record.key}`} className={styles.srOnly} />,
				disabled: record.key.toString() === '1' && roleId === 1,
				id: `rolesMembersInTableCheckbox-${record.key}`,
			};
		},
		columnWidth: 50,
		selectedRowKeys: selectedRowKeys,
	};

	const generateColumns = () => {
		const columns: ColumnsType<MemberColumn> = [
			{
				...buildColumn(_('Name'), 'Name', '200px', 'start'),
				key: 'MembersRole-Column-1',
				sorter: (a, b) => a.Name.localeCompare(b.Name),
				...SearchColumn({
					maxLength: 20,
					dataIndex: 'Name',
					reset: undefined,
					label: undefined,
					notVisible: undefined,
					resetSearch: shouldResetSearchColumn,
					clearSelection: clearSelection,
					handleResetSearch: () => setShouldResetSearchColumn(false),
					setSearchResults: (searchedValue: string) => handleOnSearch(searchedValue),
					setSearchedValue: undefined,
					searchColumnId: undefined,
					setSearchPerformed: (value: boolean) => setSearchPerformedSource(value),
				}),
			},
			{
				...buildColumn(_('Domain'), 'DirectorySource', '200px', 'start'),
				key: 'MembersRole-Column-2',
			},
			{
				...buildColumn(_('MemberOfOtherGroups'), 'BelongsToGroups', 'auto', 'start'),
				key: 'MembersRole-Column-3',
				render: (text: string) => {
					return <ExpandableContent text={text} />;
				},
			},
		];

		return columns;
	};

	const renderMembersAddModal = (): React.ReactNode => {
		if (!isOpenAddNewMemberModal) return null;
		return (
			<AddMembersModal
				nonSubscribedOperatorsIds={nonSubscribedOperatorsIds}
				onClose={() => {
					setIsOpenAddNewMemberModal(false);
				}}
				onConfirm={onConfirm}
			/>
		);
	};

	const setData = (isDirty: boolean, subscribedOperatorIds: number[]) => {
		dispatcher(setMemberContextModelAction({ isDirty: isDirty, subscribedOperatorIds: subscribedOperatorIds }));
	};

	return (
		<Spin wrapperClassName={styles.spin} size='large' tip={`${_('Loading')}...`} spinning={isTableLoading}>
			<div>
				<CloneFromRole setLoading={setIsTableLoading} tab={RoleTab.MembersPermission} setData={setData} />
				<div className={styles.buttonContainer}>
					<Button
						id='membersAddToRoleButton'
						title={`${_('AddNewMemberTo')} ${roleName}`}
						type='primary'
						className={styles.primaryButtonStyle}
						onClick={() => setIsOpenAddNewMemberModal(true)}>
						{<PlusOutlined />} {_('Member')}
					</Button>
					<Button
						id='membersSubtractFromRoleButton'
						title={`${_('RemoveMember')} ${roleName}`}
						type='default'
						disabled={selectedRowKeys?.length === 0}
						onClick={onRemoveRoleButtonClick}>
						{<MinusOutlined />} {_('Member')}
					</Button>
				</div>
				<div>
					<Table
						size='small'
						dataSource={tableData}
						columns={generateColumns()}
						rowSelection={{ ...rowSelectionMembers }}
						pagination={pagination}
						scroll={scroll}
						onChange={handleOnChangeTable}
						className={styles.tableStyle}
					/>
				</div>
				{renderMembersAddModal()}
			</div>
		</Spin>
	);
};

export { Members };
